import { useState } from "react";
import type { CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Breadcrumb } from "../components/Topbar";
import { PageHeader } from "../components/PageHeader";
import { LegalIdentityPill } from "../components/LegalIdentityPill";
import { useSlugAvailability, type SlugStatus } from "../hooks/useSlugAvailability";
import { supabase } from "../lib/supabase";
import { readFunctionErrorBody } from "../lib/functionError";
import { colors, fontSize, fontWeight, radius, space } from "../lib/tokens";
import { SUPPORTED_TIMEZONES, missingTenantIdentityFields } from "@klubora/shared";

type Archetype = "country_club" | "wine_club" | "hybrid" | "custom";
type RewardKind = "subscription_discount" | "tier" | "physical";

interface FormState {
  slug: string;
  name: string;
  legal_name: string;
  legal_cnpj: string;
  // KLUB-185 — sede, foro e encarregado nascem com o clube. O modelo antigo
  // ("cria e completa depois") deixava o primeiro documento jurídico
  // impublicável, e ninguém descobria até tentar publicar.
  legal_address: string;
  legal_jurisdiction: string;
  privacy_contact: string;
  billing_email: string;
  locale: string;
  currency: string;
  timezone: string;
  first_admin_full_name: string;
  first_admin_email: string;
  archetype: Archetype;
  invites_enabled: boolean;
  reward_enabled: boolean;
  reward_kind: RewardKind;
}

const EMPTY: FormState = {
  slug: "",
  name: "",
  legal_name: "",
  legal_cnpj: "",
  legal_address: "",
  legal_jurisdiction: "",
  privacy_contact: "",
  billing_email: "",
  locale: "pt-BR",
  currency: "BRL",
  timezone: "America/Sao_Paulo",
  first_admin_full_name: "",
  first_admin_email: "",
  archetype: "country_club",
  invites_enabled: true,
  reward_enabled: true,
  reward_kind: "tier",
};

const ARCHETYPE_OPTIONS: { value: Archetype; label: string; help: string }[] = [
  { value: "country_club", label: "Country club",     help: "Acesso físico, eventos pagos." },
  { value: "wine_club",    label: "Wine / supper club", help: "Sem espaço físico, foco em assinatura." },
  { value: "hybrid",       label: "Hybrid",             help: "Tem ambos: espaço físico + assinatura." },
  { value: "custom",       label: "Custom",             help: "Configurar manualmente depois." },
];

const REWARD_KIND_OPTIONS: { value: RewardKind; label: string }[] = [
  { value: "subscription_discount", label: "Desconto na assinatura" },
  { value: "tier",                  label: "Tier (Connector / Ambassador)" },
  { value: "physical",              label: "Brinde físico" },
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const CNPJ_RE = /^\d{14}$/;

interface ErrorPayload {
  error?: string;
  detail?: string;
  email?: string;
  /** Rótulos dos campos jurídicos faltantes (`identity_incomplete`). */
  fields?: string[];
}

interface CreateTenantResponse {
  error?: string;
  detail?: string;
  email?: string;
  fields?: string[];
  slug?: string;
  tenant_id?: string;
}

interface SeedReferralResponse {
  ok?: boolean;
  error?: string;
  detail?: string;
  applied_overrides?: number;
}

function humanizeError(payload: ErrorPayload | null, fallback?: string): string {
  const code = payload?.error;
  switch (code) {
    case "slug_taken":
      return "Slug já em uso por outro tenant. Escolha outro.";
    case "first_admin_email_taken":
      return `Email "${payload?.email ?? "informado"}" já existe na plataforma (provavelmente é membro de outro tenant). Use outro email para o primeiro admin.`;
    case "auth_user_create_failed":
      return `Falha ao criar usuário de auth: ${payload?.detail ?? "erro desconhecido"}.`;
    case "skeleton_failed":
      return `Falha no provisionamento atômico do tenant: ${payload?.detail ?? "erro desconhecido"}.`;
    case "provisioning_failed":
      return `Provisionamento falhou após o esqueleto ser criado (cleanup já rodou): ${payload?.detail ?? "erro desconhecido"}.`;
    case "missing_body":
    case "invalid_slug":
    case "invalid_name":
    case "invalid_billing_email":
    case "invalid_first_admin_full_name":
    case "invalid_first_admin_email":
    case "invalid_legal_cnpj":
    case "invalid_legal_name":
    case "invalid_legal_address":
    case "invalid_legal_jurisdiction":
    case "invalid_privacy_contact":
    case "invalid_locale":
    case "invalid_currency":
    case "invalid_timezone":
      return `Validação: ${code.replace(/_/g, " ")}.`;
    case "identity_incomplete":
      // O servidor nomeia TODOS os faltantes de uma vez.
      return `Cadastro jurídico incompleto: ${
        payload?.fields?.join(", ") ?? "campos obrigatórios"
      }.`;
    default:
      if (code) return `${code}${payload?.detail ? `: ${payload.detail}` : ""}`;
      return fallback ?? "Erro desconhecido ao criar tenant.";
  }
}

function slugStatusLabel(status: SlugStatus): { text: string; color: string } | null {
  switch (status) {
    case "available":      return { text: "disponível",       color: colors.success };
    case "taken":          return { text: "em uso",           color: colors.error };
    case "invalid_format": return { text: "formato inválido", color: colors.warning };
    case "checking":       return { text: "verificando…",     color: colors.stone };
    default:               return null;
  }
}

export function CreateTenantPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const slugStatus = useSlugAvailability(form.slug);
  const slugLabel = slugStatusLabel(slugStatus);

  const cnpjValid = CNPJ_RE.test(form.legal_cnpj);
  // KLUB-185 — espelha a borda: a mesma lista que `platform-create-tenant`
  // exige, contada aqui para o botão não prometer uma criação que o servidor
  // vai recusar com `identity_incomplete`.
  const legalMissing = missingTenantIdentityFields(form);
  const canSubmit =
    slugStatus === "available" &&
    form.name.trim().length > 0 &&
    EMAIL_RE.test(form.billing_email) &&
    cnpjValid &&
    legalMissing.length === 0 &&
    form.first_admin_full_name.trim().length > 0 &&
    EMAIL_RE.test(form.first_admin_email) &&
    !submitting;

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setServerError(null);
  }

  async function doSubmit() {
    setSubmitting(true);
    setServerError(null);
    try {
      const { data, error } = await supabase.functions.invoke("platform-create-tenant", {
        body: {
          slug: form.slug,
          name: form.name,
          legal_name: form.legal_name,
          legal_cnpj: form.legal_cnpj,
          legal_address: form.legal_address,
          legal_jurisdiction: form.legal_jurisdiction,
          privacy_contact: form.privacy_contact,
          billing_email: form.billing_email,
          locale: form.locale,
          currency: form.currency,
          timezone: form.timezone,
          first_admin_full_name: form.first_admin_full_name,
          first_admin_email: form.first_admin_email,
        },
      });
      // Toda recusa desta função é 4xx, e o supabase-js entrega 4xx como
      // `error` com `data: null`: sem ler o corpo de `error.context`, o
      // `humanizeError` recebe `null` e cai no fallback — a mesma frase para
      // slug em uso, email já cadastrado e cadastro jurídico incompleto.
      const payload = error
        ? await readFunctionErrorBody<CreateTenantResponse>(error)
        : ((data ?? null) as CreateTenantResponse | null);
      if (error || payload?.error) {
        setServerError(humanizeError(payload, error?.message));
        return;
      }
      if (!payload?.slug || !payload?.tenant_id) {
        setServerError("Resposta sem slug ou tenant_id — verifique no painel se o tenant foi criado.");
        return;
      }

      const seedRes = await supabase.functions.invoke<SeedReferralResponse>(
        "seed-referral-defaults",
        {
          body: {
            tenant_id: payload.tenant_id,
            archetype: form.archetype,
            overrides: form.archetype === "custom"
              ? undefined
              : {
                  invites_enabled: form.invites_enabled,
                  reward_enabled: form.reward_enabled,
                  reward_kind: form.reward_enabled ? form.reward_kind : null,
                },
          },
        },
      );
      // KLUB-186 — a razão da falha vem no CORPO, e o corpo não vem em `data`.
      //
      // O encadeamento aqui era `data?.detail ?? data?.error ?? error?.message`,
      // que lê como cuidado e não é: os dois primeiros elos leem `data`, e
      // `data` é `null` exatamente no ramo em que esta linha roda. A expressão
      // sempre caía no terceiro elo — a constante "non-2xx status code" do
      // cliente — e arquétipo recusado, permissão negada e falha de banco
      // produziam a mesma frase. É o pior lugar para perder a razão: o clube já
      // foi criado, e o operador precisa decidir se reconfigura à mão ou se há
      // algo quebrado antes de criar o próximo.
      const seedBody = seedRes.error
        ? await readFunctionErrorBody<SeedReferralResponse>(seedRes.error)
        : seedRes.data;
      if (seedRes.error || seedBody?.error) {
        setServerError(
          `Tenant criado, mas falha ao aplicar config de indicação: ${
            seedBody?.detail ?? seedBody?.error ?? seedRes.error?.message ?? "erro desconhecido"
          }. Configure manualmente em Settings → Indicação.`,
        );
        return;
      }

      navigate(`/tenants/${payload.slug}`);
    } catch (e) {
      setServerError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  }

  return (
    <Layout breadcrumbs={<Breadcrumb items={[{ label: "Tenants", to: "/tenants" }, { label: "Novo tenant" }]} />}>
      <PageHeader
        title="Novo tenant"
        subtitle="Cria o esqueleto do tenant e envia convite ao primeiro admin."
      />

      {serverError && (
        <div style={s.error}>Erro ao criar tenant: {serverError}</div>
      )}

      {/* Card 1 — Identidade do tenant */}
      <div style={s.card}>
        <div style={s.cardHead}>
          <h4 style={s.cardTitle}>Identidade do tenant</h4>
          <LegalIdentityPill missing={legalMissing} />
        </div>
        <p style={s.cardSub}>
          Dados B2B negociados no contrato com o cliente. O clube não é criado sem o cadastro
          jurídico completo — ele alimenta a fatura da Klubora e os textos legais do clube.
        </p>

        <div style={s.row2}>
          <div>
            <label style={s.label}>slug<span style={s.req}>*</span></label>
            <div style={s.slugWrap}>
              <input
                style={{ ...s.input, paddingRight: 90 }}
                placeholder="clubex"
                value={form.slug}
                onChange={(e) => update("slug", e.target.value.toLowerCase().trim())}
                autoComplete="off"
              />
              {slugLabel && (
                <span style={{ ...s.slugStatus, color: slugLabel.color }}>{slugLabel.text}</span>
              )}
            </div>
            <div style={s.help}>3-32 chars, [a-z0-9-]. Define subdomain (slug.klubora.com) e deep link.</div>
          </div>
          <div>
            <label style={s.label}>nome comercial<span style={s.req}>*</span></label>
            <input style={s.input} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Clube X" />
          </div>
        </div>

        <div style={{ ...s.row2, marginBottom: space.md }}>
          <div>
            <label style={s.label}>razão social<span style={s.req}>*</span></label>
            <input style={s.input} value={form.legal_name} onChange={(e) => update("legal_name", e.target.value)} placeholder="Clube X Ltda" />
          </div>
          <div>
            <label style={s.label}>CNPJ<span style={s.req}>*</span></label>
            <input
              style={s.input}
              value={form.legal_cnpj}
              onChange={(e) => update("legal_cnpj", e.target.value.replace(/\D/g, "").slice(0, 14))}
              placeholder="00000000000100"
              inputMode="numeric"
            />
            {form.legal_cnpj && !cnpjValid && <div style={s.helpError}>CNPJ deve ter 14 dígitos.</div>}
          </div>
        </div>

        <div style={{ ...s.row2, marginBottom: space.md }}>
          <div>
            <label style={s.label}>billing email<span style={s.req}>*</span></label>
            <input style={s.input} value={form.billing_email} onChange={(e) => update("billing_email", e.target.value)} placeholder="financeiro@clubex.com" />
            <div style={s.help}>Para onde a Klubora envia faturas B2B.</div>
          </div>
          <div>
            <label style={s.label}>encarregado de dados<span style={s.req}>*</span></label>
            <input style={s.input} value={form.privacy_contact} onChange={(e) => update("privacy_contact", e.target.value)} placeholder="privacidade@clubex.com" />
            <div style={s.help}>Publicado na Política de Privacidade do clube.</div>
          </div>
        </div>

        <div style={{ ...s.rowWide, marginBottom: space.md }}>
          <div>
            <label style={s.label}>endereço da sede<span style={s.req}>*</span></label>
            <input style={s.input} value={form.legal_address} onChange={(e) => update("legal_address", e.target.value)} placeholder="Rua, número, bairro, cidade/UF, CEP" />
            <div style={s.help}>Como deve aparecer nos documentos legais.</div>
          </div>
          <div>
            <label style={s.label}>comarca do foro<span style={s.req}>*</span></label>
            <input style={s.input} value={form.legal_jurisdiction} onChange={(e) => update("legal_jurisdiction", e.target.value)} placeholder="São Paulo/SP" />
            <div style={s.help}>Eleita nos Termos de Uso.</div>
          </div>
        </div>

        <div style={s.row3}>
          <div>
            <label style={s.label}>locale</label>
            <select style={s.input} value={form.locale} onChange={(e) => update("locale", e.target.value)}>
              <option value="pt-BR">pt-BR</option>
            </select>
          </div>
          <div>
            <label style={s.label}>currency</label>
            <select style={s.input} value={form.currency} onChange={(e) => update("currency", e.target.value)}>
              <option value="BRL">BRL</option>
            </select>
          </div>
          <div>
            <label style={s.label}>timezone</label>
            <select style={s.input} value={form.timezone} onChange={(e) => update("timezone", e.target.value)}>
              {SUPPORTED_TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Card 2 — Primeiro admin */}
      <div style={s.card}>
        <h4 style={s.cardTitle}>Primeiro admin</h4>
        <p style={s.cardSub}>Quem vai operar o admin do tenant. Receberá email da Klubora com link para definir senha.</p>

        <div style={s.row2}>
          <div>
            <label style={s.label}>nome completo<span style={s.req}>*</span></label>
            <input style={s.input} value={form.first_admin_full_name} onChange={(e) => update("first_admin_full_name", e.target.value)} placeholder="João da Silva" />
          </div>
          <div>
            <label style={s.label}>email<span style={s.req}>*</span></label>
            <input style={s.input} value={form.first_admin_email} onChange={(e) => update("first_admin_email", e.target.value)} placeholder="joao@clubex.com" />
          </div>
        </div>
      </div>

      {/* Card 3 — Indicação & Convites */}
      <div style={s.card}>
        <h4 style={s.cardTitle}>Indicação & Convites</h4>
        <p style={s.cardSub}>
          Aplica defaults do programa de indicação por arquétipo. Tenant pode refinar depois em Settings → Indicação.
        </p>

        <div style={{ marginBottom: space.xl }}>
          <label style={s.label}>arquétipo<span style={s.req}>*</span></label>
          <div style={s.radioGroup}>
            {ARCHETYPE_OPTIONS.map((opt) => (
              <label key={opt.value} style={s.radioRow}>
                <input
                  type="radio"
                  name="archetype"
                  value={opt.value}
                  checked={form.archetype === opt.value}
                  onChange={() => update("archetype", opt.value)}
                  style={s.radioInput}
                />
                <span style={s.radioLabel}>{opt.label}</span>
                <span style={s.radioHelp}>{opt.help}</span>
              </label>
            ))}
          </div>
        </div>

        {form.archetype !== "custom" && (
          <>
            <div style={s.toggleRow}>
              <input
                type="checkbox"
                id="invites_enabled"
                checked={form.invites_enabled}
                onChange={(e) => update("invites_enabled", e.target.checked)}
                style={s.checkbox}
              />
              <label htmlFor="invites_enabled" style={s.toggleLabel}>
                Habilitar convites
                <span style={s.toggleHelp}>
                  Membros podem convidar pessoas via app. Desativa todos os contextos (open / event / space).
                </span>
              </label>
            </div>

            <div style={s.toggleRow}>
              <input
                type="checkbox"
                id="reward_enabled"
                checked={form.reward_enabled}
                onChange={(e) => update("reward_enabled", e.target.checked)}
                style={s.checkbox}
              />
              <label htmlFor="reward_enabled" style={s.toggleLabel}>
                Habilitar reward para indicador
                <span style={s.toggleHelp}>
                  Quem indica ganha algo quando o convidado ativa.
                </span>
              </label>
            </div>

            {form.reward_enabled && (
              <div style={s.rewardKindGroup}>
                <label style={s.label}>tipo de reward</label>
                <div style={s.radioGroup}>
                  {REWARD_KIND_OPTIONS.map((opt) => (
                    <label key={opt.value} style={s.radioRow}>
                      <input
                        type="radio"
                        name="reward_kind"
                        value={opt.value}
                        checked={form.reward_kind === opt.value}
                        onChange={() => update("reward_kind", opt.value)}
                        style={s.radioInput}
                      />
                      <span style={s.radioLabel}>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {form.archetype === "custom" && (
          <div style={s.customNote}>
            Modo custom: nenhum default será aplicado além de <code>referral.archetype = 'custom'</code>.
            Configure todas as opções em Settings → Indicação após o tenant ser criado.
          </div>
        )}
      </div>

      <div style={s.footer}>
        <button type="button" style={s.btnSecondary} onClick={() => navigate("/")}>Cancelar</button>
        <button
          type="button"
          disabled={!canSubmit}
          style={canSubmit ? s.btnPrimary : { ...s.btnPrimary, ...s.btnDisabled }}
          onClick={() => setConfirmOpen(true)}
        >
          {submitting ? "Criando…" : "Criar tenant e enviar convite →"}
        </button>
      </div>

      {confirmOpen && (
        <div style={s.modalOverlay} onClick={() => !submitting && setConfirmOpen(false)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={s.modalTitle}>Confirmar criação</h3>
            <p style={s.modalText}>
              Vai criar tenant <strong>{form.name}</strong> com slug <code>{form.slug}</code>. Email com link
              de &ldquo;definir senha&rdquo; será enviado para <strong>{form.first_admin_email}</strong>.
            </p>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} disabled={submitting} onClick={() => setConfirmOpen(false)}>Cancelar</button>
              <button type="button" style={s.btnPrimary} onClick={doSubmit} disabled={submitting}>
                {submitting ? "Criando…" : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const s: Record<string, CSSProperties> = {
  error: { padding: 14, marginBottom: space.lg, background: colors.errorSoft, border: `1px solid ${colors.error}`, borderRadius: radius.md, color: colors.error, fontSize: fontSize.sm },
  card: { background: colors.paper, border: `1px solid ${colors.fog}`, borderRadius: radius.lg, padding: space.xxl, marginBottom: space.lg },
  cardTitle: { fontSize: fontSize.base, fontWeight: fontWeight.semibold, color: colors.ink, margin: `0 0 ${space.xs}px` },
  cardSub: { fontSize: fontSize.sm, color: colors.stone, margin: `0 0 ${space.xl}px` },
  row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: space.md, marginBottom: space.md },
  row3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: space.md },
  rowWide: { display: "grid", gridTemplateColumns: "2fr 1fr", gap: space.md },
  cardHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: space.sm },
  label: { display: "block", fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.ink, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 },
  req: { color: colors.error, marginLeft: 2 },
  input: { width: "100%", height: 36, padding: "0 12px", border: `1px solid ${colors.fog}`, borderRadius: radius.sm, fontSize: fontSize.md, color: colors.ink, background: colors.paper, boxSizing: "border-box", fontFamily: "inherit" },
  slugWrap: { position: "relative" },
  slugStatus: { position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: fontSize.xs, fontWeight: fontWeight.medium },
  help: { fontSize: fontSize.xs, color: colors.stone, marginTop: 4, lineHeight: 1.4 },
  helpError: { fontSize: fontSize.xs, color: colors.error, marginTop: 4 },
  footer: { display: "flex", justifyContent: "flex-end", gap: space.sm, marginTop: space.xxl },
  btnSecondary: { padding: "0 16px", height: 36, background: "transparent", border: `1px solid ${colors.fog}`, borderRadius: radius.sm, color: colors.stone, fontSize: fontSize.md, fontWeight: fontWeight.medium, cursor: "pointer" },
  btnPrimary: { padding: "0 18px", height: 36, background: colors.indigo, border: 0, borderRadius: radius.sm, color: colors.white, fontSize: fontSize.md, fontWeight: fontWeight.medium, cursor: "pointer" },
  btnDisabled: { background: colors.fog, color: colors.stone, cursor: "not-allowed" },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(14, 17, 22, 0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100 },
  modal: { background: colors.white, borderRadius: radius.lg, padding: space.xxl, maxWidth: 480, width: "90%", boxShadow: "0 12px 48px rgba(0,0,0,0.18)" },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: colors.ink, margin: `0 0 ${space.md}px` },
  modalText: { fontSize: fontSize.md, color: colors.ink, lineHeight: 1.6, margin: `0 0 ${space.xl}px` },
  modalFooter: { display: "flex", justifyContent: "flex-end", gap: space.sm },
  radioGroup: { display: "flex", flexDirection: "column", gap: space.sm },
  radioRow: { display: "flex", alignItems: "baseline", gap: space.sm, cursor: "pointer", padding: `${space.xs}px ${space.sm}px`, borderRadius: radius.sm },
  radioInput: { width: 14, height: 14, margin: 0, cursor: "pointer", accentColor: colors.indigo },
  radioLabel: { fontSize: fontSize.md, color: colors.ink, fontWeight: fontWeight.medium },
  radioHelp: { fontSize: fontSize.sm, color: colors.stone },
  toggleRow: { display: "flex", alignItems: "flex-start", gap: space.sm, marginBottom: space.md },
  checkbox: { width: 16, height: 16, marginTop: 3, cursor: "pointer", accentColor: colors.indigo },
  toggleLabel: { fontSize: fontSize.md, color: colors.ink, fontWeight: fontWeight.medium, cursor: "pointer", display: "flex", flexDirection: "column", gap: 2 },
  toggleHelp: { fontSize: fontSize.sm, color: colors.stone, fontWeight: fontWeight.regular, lineHeight: 1.4 },
  rewardKindGroup: { marginLeft: 24, marginTop: -space.xs, marginBottom: space.md, paddingLeft: space.md, borderLeft: `2px solid ${colors.fog}` },
  customNote: { padding: space.md, background: colors.paper, border: `1px solid ${colors.fog}`, borderRadius: radius.sm, fontSize: fontSize.sm, color: colors.stone, lineHeight: 1.5 },
};
